import { Timer, Assert, ObjectCE, E } from "js-vextensions";
import { manager } from "../../Manager.js";
import { g } from "../../PrivateExports.js";
export let youtubeAPILoadStarted = false;
export let youtubeAPIReady = false;
export const onYoutubeAPIReadyListeners = [];
g.onYouTubeIframeAPIReady = () => {
    youtubeAPIReady = true;
    console.log("Youtube API ready.");
    onYoutubeAPIReadyListeners.forEach(a => a());
};
export function EnsureYoutubeAPIReady() {
    return new Promise((resolve, reject) => {
        if (youtubeAPIReady) {
            resolve();
        }
        else {
            // if youtube-api loading hasn't started yet, start loading youtube API
            if (!youtubeAPILoadStarted) {
                document.head.appendChild(ObjectCE(document.createElement("script")).VSet({ src: "https://www.youtube.com/iframe_api" }));
                youtubeAPILoadStarted = true;
            }
            onYoutubeAPIReadyListeners.push(resolve);
        }
    });
}
export var YoutubePlayerState;
(function (YoutubePlayerState) {
    /** Means "we're about to play a new file". */
    YoutubePlayerState[YoutubePlayerState["UNSTARTED"] = -1] = "UNSTARTED";
    YoutubePlayerState[YoutubePlayerState["ENDED"] = 0] = "ENDED";
    YoutubePlayerState[YoutubePlayerState["PLAYING"] = 1] = "PLAYING";
    YoutubePlayerState[YoutubePlayerState["PAUSED"] = 2] = "PAUSED";
    YoutubePlayerState[YoutubePlayerState["BUFFERING"] = 3] = "BUFFERING";
    YoutubePlayerState[YoutubePlayerState["CUED"] = 5] = "CUED";
})(YoutubePlayerState || (YoutubePlayerState = {}));
export var YoutubeQuality;
(function (YoutubeQuality) {
    YoutubeQuality[YoutubeQuality["default"] = 10] = "default";
    YoutubeQuality[YoutubeQuality["small"] = 20] = "small";
    YoutubeQuality[YoutubeQuality["medium"] = 30] = "medium";
    YoutubeQuality[YoutubeQuality["large"] = 40] = "large";
    YoutubeQuality[YoutubeQuality["hd720"] = 50] = "hd720";
    YoutubeQuality[YoutubeQuality["hd1080"] = 60] = "hd1080";
    YoutubeQuality[YoutubeQuality["highres"] = 70] = "highres";
})(YoutubeQuality || (YoutubeQuality = {}));
export var YoutubeSpeed;
(function (YoutubeSpeed) {
    YoutubeSpeed[YoutubeSpeed["25%"] = 0.25] = "25%";
    YoutubeSpeed[YoutubeSpeed["50%"] = 0.5] = "50%";
    YoutubeSpeed[YoutubeSpeed["75%"] = 0.75] = "75%";
    YoutubeSpeed[YoutubeSpeed["100%"] = 1] = "100%";
    YoutubeSpeed[YoutubeSpeed["125%"] = 1.25] = "125%";
    YoutubeSpeed[YoutubeSpeed["150%"] = 1.5] = "150%";
    YoutubeSpeed[YoutubeSpeed["200%"] = 2] = "200%";
})(YoutubeSpeed || (YoutubeSpeed = {}));
export const YoutubeClipInfo_props = ["videoID", "startTime", "endTime", "quality"];
export class YoutubePlayer {
    constructor(name) {
        this.hasOwner = true;
        this.uiWidth = "100%";
        this.uiHeight = "100%";
        this.ready = false;
        this.readyListeners = [];
        this.stateListeners = [];
        this.UpdateCurrentPos_triggerOnPosChangeEvenIfNotPlaying = true;
        this.currentPosition = 0;
        this.positionUpdateTimer = new Timer(100, () => {
            if (!this.ready)
                return;
            if (this.state != YoutubePlayerState.PLAYING)
                return; // this is needed, since getCurrentTime() doesn't reflect changes from SetPosition calls, while video isn't running
            this.NotifyCurrentPosChanged(this.internalPlayer.getCurrentTime(), "playback");
        });
        this.loop = false; // must be manually set, after LoadVideo() is called, but before Play() is called (well, if you want it to work reliably)
        this.id = ++YoutubePlayer.lastID;
        this.name = name;
        youtubePlayers.push(this);
    }
    GetPlayerUI() { return this.playerUI; }
    AssertReady() {
        Assert(this.internalPlayer && this.ready, "Must first call, and await completion of, the asynchronous player.EnsureReady().");
    }
    EnsureReady() {
        return new Promise(async (resolve, reject) => {
            var _a;
            if (this.ready)
                resolve();
            this.readyListeners.push(resolve);
            if (this.internalPlayer == null) {
                await EnsureYoutubeAPIReady();
                //const playersHolder = document.querySelector("#youtube-players");
                this.containerUI = (_a = this.containerUI) !== null && _a !== void 0 ? _a : manager.GetYoutubePlayerPoolContainer(); // if func needed, but user left null, they'll see error here
                const newPlayerDiv = document.createElement("div");
                newPlayerDiv.id = `youtube-player-${this.id}`;
                this.containerUI.appendChild(newPlayerDiv);
                this.internalPlayer = new g.YT.Player(newPlayerDiv.id, E(this.playerConfig, {
                    width: this.uiWidth,
                    height: this.uiHeight,
                    events: {
                        onReady: () => {
                            this.ready = true;
                            this.readyListeners.forEach(a => a());
                        },
                        onStateChange: e => this.OnStateChange(e),
                    },
                }));
                this.playerUI = document.getElementById(newPlayerDiv.id); // get iframe that replaced the div
            }
        });
    }
    AddStateListener(listener) { this.stateListeners.push(listener); }
    RemoveStateListener(listener) { this.stateListeners.Remove(listener); }
    OnStateChange({ data: state }) {
        this.state = state;
        //Log(`[${this.id}${this.name ? `, ${this.name}` : ""}] State: ${YoutubePlayerState[state]}`);
        for (const listener of this.stateListeners) {
            listener(state, () => this.RemoveStateListener(listener));
        }
        if (state == YoutubePlayerState.PLAYING) {
            this.positionUpdateTimer.Start();
        }
        else {
            this.positionUpdateTimer.Stop();
            this.NotifyCurrentPosChanged(this.internalPlayer.getCurrentTime(), "playback"); // update one last time
            // if ended naturally (reached end of video), possibly clear buffer (depending on youtube-buffered-players setting)
            if (state == YoutubePlayerState.ENDED) {
                this.OnEndReached();
            }
        }
    }
    NotifyCurrentPosChanged(newPos, source) {
        this.currentPosition = newPos;
        if (this.onPositionChanged)
            this.onPositionChanged(this.currentPosition, source);
        if (this.loadedClipInfo && this.loadedClipInfo.endTime && this.currentPosition >= this.loadedClipInfo.endTime) {
            this.OnEndReached();
        }
    }
    OnEndReached() {
        var _a;
        // if null, clip must have ended while we were trying to stop/switch the video; just do nothing
        if (this.loadedClipInfo == null)
            return;
        if (this.loop) {
            this.SetPosition((_a = this.loadedClipInfo.startTime) !== null && _a !== void 0 ? _a : 0);
            this.Play();
        }
        else {
            this.PauseOrStop();
        }
    }
    WaitTillState(...waitForStates) {
        return new Promise((resolve, reject) => {
            this.AddStateListener((state, removeListener) => {
                if (waitForStates.Contains(state)) {
                    resolve();
                    removeListener();
                }
            });
        });
    }
    /** Promise is resolved once video has completed loading/buffering. */
    async LoadVideo(info, autoplay = false, markOwned = true) {
        this.AssertReady();
        const oldInfo = this.loadedClipInfo;
        this.loadedClipInfo = null;
        if (oldInfo == null || info.videoID != oldInfo.videoID || info.quality != oldInfo.quality) {
            //this.internalPlayer[autoplay ? "loadVideoById" : "cueVideoById"]({
            this.internalPlayer["cueVideoById"]({
                videoId: info.videoID,
                startSeconds: info.startTime || undefined,
                //endSeconds: info.endTime || undefined, // handle end-time ourselves, for consistency (and because built-in end-time doesn't seem reliable)
                suggestedQuality: YoutubeQuality[info.quality || YoutubeQuality.default],
            });
            //await this.WaitTillState(autoplay ? YoutubePlayerState.PLAYING : YoutubePlayerState.CUED);
        }
        else {
            this.internalPlayer.seekTo(info.startTime, true);
        }
        // to detect video-load completion, call play(), and wait for state to reflect (only way I know atm)
        this.internalPlayer.playVideo();
        await this.WaitTillState(YoutubePlayerState.PLAYING);
        if (!autoplay) {
            this.internalPlayer.pauseVideo();
            await this.WaitTillState(YoutubePlayerState.PAUSED);
        }
        this.loadedClipInfo = info;
        if (markOwned)
            this.hasOwner = true;
    }
    SetPlaybackRate(speed) {
        this.AssertReady();
        this.internalPlayer.setPlaybackRate(speed);
    }
    SetVolume(volume) {
        this.AssertReady();
        this.internalPlayer.setVolume(volume * 100);
    }
    /** Before calling, make sure video-load has completed, ie. "await LoadVideo()". (else, this call can fail, and even disrupt video-load's start-time) */
    SetPosition(timeInSec) {
        this.AssertReady();
        this.internalPlayer.seekTo(timeInSec, true);
        // we need to just tell it the new time, because the seekTo command does not apply instantly (which is needed for some call-paths, apparently)
        this.NotifyCurrentPosChanged(timeInSec, "setPosition");
    }
    Play() {
        this.AssertReady();
        /*if (this.lastPlayTime != null) {
            this.SetPosition(this.loadedClipInfo.startTime || 0);
        }*/
        this.internalPlayer.playVideo();
        this.lastPlayTime = Date.now();
    }
    Pause() {
        this.AssertReady();
        this.internalPlayer.pauseVideo();
    }
    StopAndClearBuffer() {
        this.AssertReady();
        this.internalPlayer.stopVideo();
        //this.UpdateCurrentPos();
        this.loadedClipInfo = null; // clear loaded-clip-info, since it doesn't reflect the (empty) buffered state anymore
    }
    // todo: fix that this does not reliably persistently-stop the player
    // (I think this is due to pause/stop being called just before it ends, triggering the looping mechanism to start it again)
    PauseOrStop() {
        // if we're within the buffered-youtube-players limit, just pause the player (for faster play again later)
        if (manager.GetYoutubePlayersToKeepBuffered && GetBufferingOrBufferedYoutubePlayers().length <= GetYoutubePlayersToKeepBuffered()) {
            this.Pause();
        }
        // else, stop and clear the buffer (this lets low-memory devices prevent out-of-memory crashes)
        else {
            this.StopAndClearBuffer();
        }
    }
}
YoutubePlayer.lastID = -1;
/* /** Key: JSON of the clip-info/config object. Value: Array of players for the key. (will be multiple if first was playing while a second play request was made) *#/
export let youtubePlayers = {};
export function GetReadyYoutubePlayerForClipInfo(info: YoutubeClipInfo) {
    let key = ToJSON(info);
    let players = youtubePlayers[key] || [];
    let readyPlayer = players.filter(a=>a.)
} */
function GetYoutubePlayersToKeepBuffered() {
    return manager.GetYoutubePlayersToKeepBuffered ? manager.GetYoutubePlayersToKeepBuffered() : -1;
}
export const youtubePlayers = [];
export function GetUnownedYoutubePlayers() { return youtubePlayers.filter(a => !a.hasOwner); }
export function GetBufferingOrBufferedYoutubePlayers() { return youtubePlayers.filter(a => [YoutubePlayerState.BUFFERING, YoutubePlayerState.PLAYING, YoutubePlayerState.PAUSED, YoutubePlayerState.ENDED].Contains(a.state)); }
export async function GetUnownedYoutubePlayerReady(preferredClipInfoMatch) {
    //let unownedPlayers = youtubePlayers.filter(a=>a.state == YoutubePlayerState.ENDED);
    // order by last-play-time, so that we prefer unowned-players which have not been used recently
    const unownedPlayers_all = GetUnownedYoutubePlayers().OrderBy(a => a.lastPlayTime);
    const unownedPlayers_matchingClip = unownedPlayers_all.filter(a => preferredClipInfoMatch && a.loadedClipInfo && a.loadedClipInfo.videoID == preferredClipInfoMatch.videoID && a.loadedClipInfo.quality == preferredClipInfoMatch.quality).OrderBy(a => a.lastPlayTime);
    // if unowned+matching player, always reuse that as it has no negative side effects
    // else, reuse an unowned+nonmatching player *if* we've reached the buffered-players limit
    // else, create a new one
    const player = unownedPlayers_matchingClip.FirstOrX() || (youtubePlayers.length >= GetYoutubePlayersToKeepBuffered() && unownedPlayers_all.FirstOrX()) || new YoutubePlayer();
    await player.EnsureReady();
    return player;
}
//# sourceMappingURL=YoutubePlayer.js.map